import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { APP_CONFIG, AppConfig } from "@src/app/app-config.module";
import { NGXLogger } from "ngx-logger";
import { Observable, OperatorFunction, throwError } from "rxjs";
import { catchError, take, tap } from "rxjs/operators";
import {
    Address, AddressSearchResult, Applicant, Application, ApplicationStartDTO, BundleInfo, BusinessActivitySurvey,
    CheckVerificationCodeRequestDTO, Company, CompanyLookupResult, CompanySearchResult, CompanyType, ComplianceData, Contact,
    CountryMetadata, CountryName, EmailVerificationData, EpaStatus, ExpectedActivitySurvey, IDVSessionDetails,
    ItalianProvince, ItalianTown, MarketingData, Town, UserIDVDetails, XeroOAuthDTO
} from "../models/data-model";
import { SoldoCookieService } from "./soldo-cookie.service";

export interface ResponseDTO<T> {
    timestamp: number;
    data: T;
}

export interface HttpOptions {
    headers?: HttpHeaders;
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
        [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    body?: any;
}

@Injectable({
    providedIn: 'root'
})
export class RegistrationApiService {
    private _applicationId: string;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cookieService: SoldoCookieService,
        private readonly router: Router,
        private readonly logger: NGXLogger,
        @Inject(APP_CONFIG) private readonly appConfig: AppConfig
    ) { }

    get applicationId(): string {
        return this._applicationId;
    }

    set applicationId(id: string) {
        this._applicationId = id;
    }

    private post<T, K>(method: string, body: K, options?: HttpOptions): Observable<T> {
        return this.httpClient.post<T>(`${this.appConfig.apiGatewayEndpoint}/${method}`, body, this.addVersionHeader(options));
    }

    private postWithAuth<T, K>(method: string, body: K, options?: HttpOptions, redirectOn401 = true): Observable<T> {
        if (!this.cookieService.hasJWT()) {
            if (redirectOn401) {
                this.router.navigate(['/auth/email-verification'], { replaceUrl: true });
            }
            return throwError(new Error(`Missing JWT for authenticated api call: ${method}`));
        }
        const call = this.post<T, K>(method, body, this.addAuthorizationHeaders(options));
        if (redirectOn401) {
            return call.pipe(
                this.catchUnauthorized<T>()
            );
        }
        return call;
    }

    private get<T>(method: string, options?: HttpOptions): Observable<T> {
        return this.httpClient.get<T>(`${this.appConfig.apiGatewayEndpoint}/${method}`, this.addVersionHeader(options));
    }

    private getWithAuth<T>(method: string, options?: HttpOptions, redirectOn401 = true): Observable<T> {
        if (!this.cookieService.hasJWT()) {
            if (redirectOn401) {
                this.router.navigate(['/auth/email-verification'], { replaceUrl: true });
            }
            return throwError(new Error(`Missing JWT for authenticated api call: ${method}`));
        }
        const call = this.get<T>(method, this.addAuthorizationHeaders(options));
        if (redirectOn401) {
            return call.pipe(
                this.catchUnauthorized<T>()
            );
        }
        return call;
    }

    private addAuthorizationHeaders(options: HttpOptions) {
        options ??= {
            headers: new HttpHeaders()
        };

        // angular/common/HttpHeader instances are immutable, set returns a clone
        let headers = options.headers ?? new HttpHeaders();
        headers = headers.set("Authorization", `Bearer ${this.cookieService.readJWT()}`);

        options.headers = headers;
        return options;
    }

    private addVersionHeader(options?: HttpOptions): HttpOptions {
        options ??= {
            headers: new HttpHeaders()
        };

        // angular/common/HttpHeader instances are immutable, set returns a clone
        let headers = options.headers ?? new HttpHeaders();
        headers = headers.set("X-SLD-RW-Version", this.appConfig.appVersion);

        options.headers = headers;
        return options;
    }

    /**
     * returns a catchError operator that in case of an HTTP 401 error code redirects
     * to the email verification page and removes the current (invalid) JWT
     */
    private catchUnauthorized<T>(): OperatorFunction<T, T> {
        return catchError(err => {
            if (err?.status === 401) {
                this.cookieService.cleanJWT();
                err.redirectToEmailVerification = this.router.navigate(['/auth/email-verification'], { replaceUrl: true });
            }
            return throwError(err);
        });
    }

    requestEmailVerificationCode(body: EmailVerificationData): Promise<void> {
        return this.post<void, EmailVerificationData>('email/verification/code/request', body)
            .pipe(
                take(1)
            ).toPromise();
    }

    checkEmailVerificationCode(body: CheckVerificationCodeRequestDTO): Promise<{ token: string, status: string }> {
        return this.post<{ token: string, status: string }, CheckVerificationCodeRequestDTO>('email/verification/code/check', body)
            .pipe(
                take(1)
            ).toPromise();
    }

    getApplicationsList(redirectWhenUnauthorized = true): Promise<Application[]> {
        return this.getWithAuth<Application[]>('application/list', null, redirectWhenUnauthorized)
            .pipe(
                take(1)
            ).toPromise();
    }

    checkPlanOfferValidity(plan: string, offer: string): Promise<BundleInfo> {
        return this.get<BundleInfo>(`billing/planAndOffer/${plan}/${offer}/valid`)
            .pipe(
                take(1)
            ).toPromise();
    }

    getPlanOfferList(): Observable<BundleInfo[]> {
        return this.get<BundleInfo[]>('billing/planAndOffer/list')
            .pipe(
                take(1)
            );
    }

    startApplication(body: ApplicationStartDTO): Promise<Application> {
        return this.postWithAuth<Application, ApplicationStartDTO>('application/start', body)
            .pipe(
                take(1),
                tap(application => {
                    this.applicationId = application.id;
                })
            ).toPromise();
    }

    getApplicant(): Promise<Applicant> {
        return this.getWithAuth<Applicant>(`application/${this.applicationId}/applicant`)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveApplicant(applicant: Applicant): Promise<void> {
        return this.postWithAuth<void, Applicant>(`application/${this.applicationId}/applicant`, applicant)
            .pipe(
                take(1)
            ).toPromise();
    }

    getApplication(): Promise<Application> {
        if (this.applicationId) {
            return this.getWithAuth<Application>(`application/${this.applicationId}`)
                .pipe(
                    take(1)
                ).toPromise();
        }
        return Promise.resolve({
            company: {},
            applicant: {}
        } as unknown as Application);
    }

    saveApplication(application: Application): Promise<void> {
        if (this.applicationId) {
            return this.postWithAuth<void, Application>(`application/${this.applicationId}`, application)
                .pipe(
                    take(1)
                ).toPromise();
        }
        this.logger.error('Missing applicationId');
        return Promise.reject(new Error('Missing application id'));
    }

    saveWebappChecklist(checklist: string): Promise<void> {
        return this.postWithAuth<void, { webappCheckList: string }>(`application/${this.applicationId}/webappCheckList`, { webappCheckList: checklist })
            .pipe(
                take(1)
            ).toPromise();
    }

    getContacts(): Promise<Contact[]> {
        return this.getWithAuth<Contact[]>(`application/${this.applicationId}/contacts`)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveAllContacts(contacts: Contact[]): Promise<Contact[]> {
        return this.postWithAuth<Contact[], Contact[]>(`application/${this.applicationId}/allContacts`, contacts)
            .pipe(
                take(1)
            ).toPromise();
    }

    getContact(id: string): Promise<Contact> {
        return this.getWithAuth<Contact>(`application/${this.applicationId}/contact/${id}`)
            .pipe(
                take(1)
            ).toPromise();
    }

    searchCompany(term: string, countryIso3: string): Promise<CompanySearchResult> {
        const options: HttpOptions = {
            params: {
                term: term,
                countryIso3: countryIso3
            }
        };
        return this.getWithAuth<CompanySearchResult>(`company/search`, options)
            .pipe(
                take(1)
            ).toPromise();
    }

    searchCompanyByName(term: string, countryIso3: string): Promise<CompanySearchResult> {
        const options: HttpOptions = {
            params: { term, countryIso3 }
        };
        return this.getWithAuth<CompanySearchResult>(`company/searchByName`, options)
            .pipe(take(1)).toPromise();
    }

    searchCompanyByRegistrationNumber(term: string, countryIso3: string): Promise<CompanySearchResult> {
        const options: HttpOptions = {
            params: { term, countryIso3 }
        };
        return this.getWithAuth<CompanySearchResult>(`company/searchByRegistrationNumber`, options)
            .pipe(take(1)).toPromise();
    }

    lookupCompany(kybProviderId: string, countryIso3: string): Promise<CompanyLookupResult> {
        const options: HttpOptions = {
            params: {
                kybProviderId: kybProviderId,
                countryIso3: countryIso3
            }
        };
        return this.getWithAuth<CompanyLookupResult>(`company/lookup`, options)
            .pipe(
                take(1)
            ).toPromise();
    }

    addressSearch(postCode: string): Promise<AddressSearchResult[]> {
        const options: HttpOptions = {
            params: {
                postCode: postCode
            }
        };
        return this.getWithAuth<AddressSearchResult[]>(`address/search`, options, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    addressLookup(addressId: string): Promise<Address> {
        const options: HttpOptions = {
            params: {
                addressId: addressId
            }
        };
        return this.getWithAuth<Address>(`address/lookup`, options, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    getCompany(): Promise<Company> {
        return this.getWithAuth<Company>(`application/${this.applicationId}/company`)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveCompany(company: Partial<Company>) {
        return this.postWithAuth<void, Partial<Company>>(`application/${this.applicationId}/company`, company)
            .pipe(
                take(1)
            ).toPromise();
    }

    getBusinessActivitySurvey(): Promise<BusinessActivitySurvey> {
        return this.getWithAuth<BusinessActivitySurvey>(`application/${this.applicationId}/businessActivitySurvey`)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveBusinessActivitySurvey(survey: BusinessActivitySurvey): Promise<void> {
        return this.postWithAuth<void, BusinessActivitySurvey>(`application/${this.applicationId}/businessActivitySurvey`, survey)
            .pipe(
                take(1)
            ).toPromise();
    }

    getExpectedActivitySurvey(): Promise<ExpectedActivitySurvey> {
        return this.getWithAuth<ExpectedActivitySurvey>(`application/${this.applicationId}/expectedActivitySurvey`)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveExpectedActivitySurvey(survey: ExpectedActivitySurvey): Promise<void> {
        return this.postWithAuth<void, ExpectedActivitySurvey>(`application/${this.applicationId}/expectedActivitySurvey`, survey)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveMarketingData(marketingData: MarketingData): Promise<void> {
        return this.postWithAuth<void, MarketingData>(`application/${this.applicationId}/marketingData`, marketingData)
            .pipe(
                take(1)
            ).toPromise();
    }

    completeApplication(discountCode?: string): Promise<ComplianceData> {
        return this.postWithAuth<ComplianceData, { discountCode: string }>(`application/${this.applicationId}/complete`, { discountCode: discountCode })
            .pipe(
                take(1)
            ).toPromise();
    }

    getComplianceData(appId?: string): Promise<ComplianceData> {
        return this.getWithAuth<ComplianceData>(`application/${appId ?? this.applicationId}/compliance`)
            .pipe(
                take(1)
            ).toPromise();
    }

    getEpaStatus(appId?: string): Promise<EpaStatus> {
        return this.getWithAuth<EpaStatus>(`application/${appId ?? this.applicationId}/isEpa`)
            .pipe(
                take(1)
            ).toPromise();
    }

    getIdvToken(idvSessionId: string): Promise<string> {
        return this.get<string>(`idv/sdk-token`, { params: { idvSessionId: idvSessionId }, responseType: 'text' } as any)
            .pipe(
                take(1)
            ).toPromise();
    }

    xeroInitSignup(): Promise<XeroOAuthDTO> {
        return this.post<XeroOAuthDTO, null>('xero/initSignup', null)
            .pipe(
                take(1)
            ).toPromise();
    }

    xeroGetUserData(data: XeroOAuthDTO): Promise<Applicant> {
        return this.post<Applicant, XeroOAuthDTO>('xero/getUserData', data)
            .pipe(
                take(1)
            ).toPromise();
    }

    saveAndContinueLater(lang: string): Promise<void> {
        return this.postWithAuth<void, { lang: string }>(`application/${this.applicationId}/saveAndContinue`, { lang: lang })
            .pipe(
                take(1)
            ).toPromise();
    }

    getCompanyTypes(country: string): Promise<CompanyType[]> {
        return this.getWithAuth<CompanyType[]>(`company/${country}/type/list`)
            .pipe(
                take(1)
            ).toPromise();
    }

    getItalianProvinces(): Promise<ItalianProvince[]> {
        return this.getWithAuth<ItalianProvince[]>('address/ITA/province/list', {}, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    getItalianTownsByProvince(province: string): Promise<ItalianTown[]> {
        return this.getWithAuth<ItalianTown[]>(`address/ITA/${province}/town/list`, {}, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    searchItalianTownByTaxCode(taxCode: string): Promise<Town> {
        return this.getWithAuth<Town>(`tax/ITA/${taxCode}`, {}, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    getWorldCountriesMetadata(): Observable<CountryMetadata[]> {
        return this.getWithAuth<CountryMetadata[]>(`address/country/list`, {}, false)
            .pipe(
                take(1)
            );
    }

    getWorldCountriesNames(lang: string): Observable<CountryName[]> {
        return this.getWithAuth<CountryName[]>(`address/country/${lang}/name/list`, {}, false)
            .pipe(
                take(1)
            );
    }

    getIdvSession(personExternalId: string): Promise<IDVSessionDetails> {
        return this.getWithAuth<IDVSessionDetails>(`user/idv/info/${personExternalId}`, {}, false)
            .pipe(
                take(1)
            ).toPromise();
    }

    postUserIdvDetails(data: UserIDVDetails, userKycRequestId: string): Promise<string> {
        return this.postWithAuth<string, UserIDVDetails>(`user/idv/submit/${userKycRequestId}`, data, {
            responseType: "text" as 'json'
        }, false).pipe(
            take(1)
        ).toPromise();
    }

}
